// Idle-session helpers for the 15-minute inactivity sign-out.
//
// The last time the user did something is kept in localStorage so that every
// open tab agrees on it, and so that a browser that was closed and reopened
// can be checked on page load. All decisions compare timestamps (never a single
// long timer), so a sleeping laptop or a throttled background tab cannot
// stretch the timeout.

export type SignOutReason = 'idle' | 'expired';
export type IdleState = 'active' | 'warning' | 'expired';

export const ACTIVITY_KEY = 'metrix.lastActivity';
export const SIGNOUT_REASON_KEY = 'metrix.signoutReason';

const MAX_IDLE_MINUTES = 15;
const MIN_IDLE_MINUTES = 0.5;
// A stored time further in the future than this means the clock was changed.
const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;
// Writes to storage are limited to one per second; mousemove/scroll can fire hundreds.
const WRITE_THROTTLE_MS = 1000;

/**
 * Idle limit in milliseconds: 15 minutes. REACT_APP_IDLE_TIMEOUT_MINUTES may
 * shorten it (handy for testing) but can never lengthen it.
 */
export function getIdleMs(): number {
  const raw = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES);
  const minutes = Number.isFinite(raw) && raw > 0 ? raw : MAX_IDLE_MINUTES;
  return Math.min(Math.max(minutes, MIN_IDLE_MINUTES), MAX_IDLE_MINUTES) * 60 * 1000;
}

/** How long before sign-out the warning appears: 60 seconds (shorter for very short test limits). */
export function getWarningLeadMs(): number {
  return Math.min(60 * 1000, getIdleMs() / 2);
}

/** Pure decision: what should the app do given the last-activity time and the current time? */
export function idleState(last: number | null, now: number): IdleState {
  if (last === null) return 'expired';
  if (last - now > MAX_CLOCK_SKEW_MS) return 'expired';
  const idle = Math.max(0, now - last);
  const limit = getIdleMs();
  if (idle >= limit) return 'expired';
  if (idle >= limit - getWarningLeadMs()) return 'warning';
  return 'active';
}

// ─── last-activity storage ────────────────────────────────────────────────────

let memoryLast: number | null = null; // fallback if localStorage is blocked
let lastWrite = 0;

export function readLastActivity(): number | null {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return memoryLast;
  }
}

/** Record that the user just did something. `force` skips the write throttle. */
export function markActivity(now: number = Date.now(), force = false): void {
  if (!force && now - lastWrite < WRITE_THROTTLE_MS && now >= lastWrite) return;
  lastWrite = now;
  memoryLast = now;
  try {
    window.localStorage.setItem(ACTIVITY_KEY, String(now));
  } catch {
    /* storage blocked: the in-memory copy still works for this tab */
  }
}

export function clearActivity(): void {
  memoryLast = null;
  lastWrite = 0;
  try {
    window.localStorage.removeItem(ACTIVITY_KEY);
  } catch {
    /* ignore */
  }
}

/** Call right after a successful sign-in. */
export function startIdleSession(now: number = Date.now()): void {
  clearSignoutReason();
  markActivity(now, true);
}

// ─── why the user was signed out (per tab, shown on the login page) ──────────

export function setSignoutReason(reason: SignOutReason): void {
  try {
    window.sessionStorage.setItem(SIGNOUT_REASON_KEY, reason);
  } catch {
    /* ignore */
  }
}

export function peekSignoutReason(): SignOutReason | null {
  try {
    const v = window.sessionStorage.getItem(SIGNOUT_REASON_KEY);
    return v === 'idle' || v === 'expired' ? v : null;
  } catch {
    return null;
  }
}

export function clearSignoutReason(): void {
  try {
    window.sessionStorage.removeItem(SIGNOUT_REASON_KEY);
  } catch {
    /* ignore */
  }
}

export function signoutMessage(reason: SignOutReason | null): string | null {
  if (reason === 'idle') return 'You were signed out after 15 minutes of inactivity. Please sign in again.';
  if (reason === 'expired') return 'Your session has ended. Please sign in again.';
  return null;
}
